import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useApp } from '../context/AppContext';
import RideCard from '../components/RideCard';

export default function MyRides() {
  const navigate = useNavigate();
  const { currentUser, rides } = useApp();
  const [tab, setTab] = useState('upcoming');

  const today = new Date().toISOString().split('T')[0];

  const categorized = useMemo(() => {
    const upcoming = [];
    const past = [];
    const driverRides = [];

    rides.forEach(r => {
      const isMyDrive = r.driverId === currentUser.id;
      const isAcceptedRider = r.requests.some(req => req.riderId === currentUser.id && req.status === 'accepted');
      const isPastDate = r.date < today;

      if (isMyDrive) {
        if (!isPastDate && r.status !== 'cancelled') {
          driverRides.push(r);
          upcoming.push({ ...r, _role: 'driver' });
        } else {
          past.push({ ...r, _role: 'driver' });
        }
      } else if (isAcceptedRider) {
        if (!isPastDate && r.status !== 'cancelled') {
          upcoming.push({ ...r, _role: 'rider' });
        } else {
          past.push({ ...r, _role: 'rider' });
        }
      }
    });

    // Pending requests
    const pending = rides.filter(r =>
      r.requests.some(req => req.riderId === currentUser.id && req.status === 'pending') &&
      r.date >= today
    );

    upcoming.sort((a, b) => a.date.localeCompare(b.date) || a.time.localeCompare(b.time));
    past.sort((a, b) => b.date.localeCompare(a.date));
    pending.sort((a, b) => a.date.localeCompare(b.date));
    driverRides.sort((a, b) => a.date.localeCompare(b.date));

    return { upcoming, past, pending, driverRides };
  }, [rides, currentUser, today]);

  const tabs = [
    { key: 'upcoming', label: 'Upcoming', count: categorized.upcoming.length },
    { key: 'pending', label: 'Requests', count: categorized.pending.length },
    { key: 'driving', label: 'Driving', count: categorized.driverRides.length },
    { key: 'past', label: 'Past', count: categorized.past.length },
  ];

  const current = {
    upcoming: categorized.upcoming,
    pending: categorized.pending,
    driving: categorized.driverRides,
    past: categorized.past,
  }[tab];

  return (
    <div className="page">
      <div style={{ paddingTop: 8, marginBottom: 20 }}>
        <h1 style={{ fontSize: 24, fontWeight: 800 }}>My Rides</h1>
      </div>

      {/* Tab bar */}
      <div style={{
        display: 'flex', gap: 6, marginBottom: 20,
        overflowX: 'auto', paddingBottom: 2,
      }}>
        {tabs.map(t => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            style={{
              padding: '8px 14px', borderRadius: 20, fontSize: 13, fontWeight: 600,
              whiteSpace: 'nowrap', flexShrink: 0,
              background: tab === t.key ? 'var(--accent)' : 'var(--navy-light)',
              color: tab === t.key ? 'white' : 'var(--gray-400)',
              border: `1px solid ${tab === t.key ? 'var(--accent)' : 'var(--navy-border)'}`,
              transition: 'all 0.2s',
            }}
          >
            {t.label}
            {t.count > 0 && (
              <span style={{
                marginLeft: 6, background: tab === t.key ? 'rgba(255,255,255,0.25)' : 'var(--navy-border)',
                borderRadius: 10, padding: '1px 6px', fontSize: 11,
              }}>
                {t.count}
              </span>
            )}
          </button>
        ))}
      </div>

      {/* Pending requests note */}
      {tab === 'pending' && categorized.pending.length > 0 && (
        <div className="alert alert-info" style={{ marginBottom: 16 }}>
          These rides are awaiting driver approval.
        </div>
      )}

      {/* Rides list */}
      {current.length === 0 ? (
        <div style={{
          background: 'var(--navy-light)', border: '1px solid var(--navy-border)',
          borderRadius: 'var(--radius)', padding: '40px 24px', textAlign: 'center',
        }}>
          <div style={{ fontSize: 40, marginBottom: 12 }}>
            {tab === 'upcoming' ? '🗓️' : tab === 'pending' ? '⏳' : tab === 'driving' ? '🚗' : '✓'}
          </div>
          <p style={{ fontWeight: 600, marginBottom: 8 }}>
            {tab === 'upcoming' ? 'No upcoming rides' :
             tab === 'pending' ? 'No pending requests' :
             tab === 'driving' ? 'No rides posted' : 'No past rides'}
          </p>
          <p style={{ color: 'var(--gray-400)', fontSize: 13, marginBottom: 16 }}>
            {tab === 'upcoming' || tab === 'pending'
              ? 'Browse available rides to get started.'
              : tab === 'driving'
              ? 'Post your first ride and offer seats to students.'
              : 'Your completed rides will appear here.'}
          </p>
          {(tab === 'upcoming' || tab === 'pending') && (
            <button className="btn btn-primary" onClick={() => navigate('/browse')}>
              Browse Rides
            </button>
          )}
          {tab === 'driving' && currentUser.isDriver && (
            <button className="btn btn-primary" onClick={() => navigate('/post-ride')}>
              Post a Ride
            </button>
          )}
        </div>
      ) : (
        <>
          {tab === 'driving'
            ? current.map(r => (
                <div key={r.id}>
                  <RideCard ride={r} showDriver={false} />
                  {r.requests.some(req => req.status === 'pending') && (
                    <div style={{
                      marginTop: -6, marginBottom: 12, padding: '6px 14px',
                      background: 'var(--accent-light)',
                      border: '1px solid rgba(59,130,246,0.3)',
                      borderTop: 'none',
                      borderRadius: '0 0 var(--radius) var(--radius)',
                      fontSize: 12, color: 'var(--accent)', fontWeight: 600,
                    }}>
                      🔔 {r.requests.filter(req => req.status === 'pending').length} pending request(s) — tap to manage
                    </div>
                  )}
                </div>
              ))
            : current.map(r => <RideCard key={r.id} ride={r} />)
          }
        </>
      )}
    </div>
  );
}
